import { calculateShippingCost, calculateTaxCost } from "../../util/pricing";
import { formatMoney, formatMoneyShort } from "../../util/strings";
import {
  removeDiscountCode,
  saveDiscountCode,
} from "../../lib/cart/CartStorage";
import { useContext, useState } from "react";

import Button from "../Button";
import { CartContext } from "../../contexts/CartContext";
import CartQuotePdf from "../../pdfs/CartQuotePdf";
import ErrorsList from "../ErrorsList";
import FontFamilySection from "./FontFamilySection";
import FoundryClient from "../../lib/foundry_client/FoundryClient";
import Loader from "../Loader";
import { PDFDownloadLink } from "@react-pdf/renderer";
import PhysicalProductSection from "./PhysicalProductSection";
import { PhysicalProductState } from "../../lib/models/PhysicalProducts";
import { PhysicalProductsContext } from "../../contexts/PhysicalProductsContext";
import Routes from "../../routes";
import { ShareCartContext } from "../../contexts/ShareCartContext";
import dayjs from "dayjs";
import { getDiscount } from "../../util/discounts";
import { push } from "../../util/routing";
import { toast } from "react-toastify";
import { useRouter } from "next/router";
import { useWindowSize } from "@uidotdev/usehooks";

interface Props {
  onlyShowTotals?: boolean;
  action: "review" | "checkout" | null;
  checkoutDetails?: {
    shippingMethod: string;
    addressState?: string;
  };
}

export default function ShoppingCart(props: Props) {
  const router = useRouter();
  const { cartDetails, propagateCartDetails } = useContext(CartContext);
  const { shareCartId, setShareCartId } = useContext(ShareCartContext);
  const { physicalProducts } = useContext(PhysicalProductsContext);
  const [expandedState, setExpandedState] = useState<boolean | null>(null);
  const size = useWindowSize();

  let expanded = props.onlyShowTotals ? true : expandedState;

  if (expanded === null) {
    // Set default
    expanded = size.width ? size.width > 1024 : true;
  }

  async function handleClick(event) {
    event.preventDefault();

    if (props.action === "review") {
      push(router, Routes.cart.index);
      return;
    }

    const errors =
      cartDetails?.getInvalidPhysicalProductQuantities(physicalProducts) || [];

    if (errors.length > 0) {
      toast(
        <ErrorsList title="Failed to checkout" errors={errors}></ErrorsList>,
        { autoClose: false }
      );
      return;
    }

    push(router, Routes.cart.checkoutIndex);
  }

  async function handleShareCart() {
    if (!cartDetails) {
      return;
    }

    if (!shareCartId) {
      const res = await FoundryClient.shareCart(cartDetails);
      setShareCartId(res.cartId);
    }
  }

  const cartCount = cartDetails?.itemCount();
  const [_lineItems, totalDetails] = cartDetails
    ? cartDetails.totalDetails(physicalProducts)
    : [null, null];

  const productDiscounts = cartDetails
    ? cartDetails.physicalProductDiscounts(physicalProducts)
    : {};

  const activeDiscount = getDiscount(cartDetails?.discountCode || "");
  const activeDiscountCost =
    totalDetails && activeDiscount?.discountPercentage
      ? Math.ceil(
          (totalDetails.total * activeDiscount.discountPercentage) / 100
        )
      : 0;

  const shippingCost =
    props.checkoutDetails &&
    cartDetails &&
    totalDetails &&
    cartDetails.physicalProducts.length > 0 &&
    !activeDiscount?.freeShipping
      ? calculateShippingCost(
          totalDetails.total - activeDiscountCost,
          props.checkoutDetails.shippingMethod
        )
      : null;

  const taxCost =
    shippingCost != null &&
    cartDetails &&
    totalDetails &&
    cartDetails.physicalProducts.length > 0 &&
    props.checkoutDetails?.addressState
      ? calculateTaxCost(
          totalDetails.total - activeDiscountCost + shippingCost,
          props.checkoutDetails.addressState
        )
      : 0;

  if (physicalProducts.length === 0) {
    <div className="w-full pt-48 flex items-center justify-center">
      <Loader size={90} />
    </div>;
  }

  return (
    <div className="bg-secondary flex flex-col w-full h-full lg:pl-10 lg:pr-2 lg:border-l">
      {cartDetails && totalDetails && cartCount ? (
        <>
          <section className="w-full">
            <h3 className="t5">
              {props.onlyShowTotals ? "Summary" : "Your Cart"}{" "}
              {!props.onlyShowTotals && (
                <span
                  className={`text-xs opacity-50 cursor-pointer`}
                  onClick={() =>
                    !props.onlyShowTotals && setExpandedState(!expanded)
                  }
                >
                  {expanded ? "(hide)" : "(show)"}
                </span>
              )}
            </h3>
            <div className={`my-4 flex flex-col ${expanded ? "" : "hidden"}`}>
              <progress
                value={(totalDetails.total - activeDiscountCost) / 5000}
                className="w-full mt-2 mb-3 rounded-lg h-3"
              />
              <div className="flex justify-between text-sm gap-1">
                <span className="flex-1">
                  <div className="flex gap-2">
                    <span>✿</span>
                    <span>
                      {totalDetails.total - activeDiscountCost >= 5000 ||
                      activeDiscount?.freeShipping
                        ? "You qualify for free shipping!"
                        : `You are ${formatMoney(
                            "USD",
                            5000 - (totalDetails.total - activeDiscountCost),
                            true
                          )} away from free shipping.`}
                    </span>
                  </div>
                </span>
                {totalDetails.total - activeDiscountCost < 5000 && (
                  <span>
                    {formatMoney(
                      "USD",
                      totalDetails.total - activeDiscountCost,
                      true
                    )}{" "}
                    / $50
                  </span>
                )}
              </div>
            </div>
          </section>
          <hr
            className={`my-4 w-full flex-row items-center justify-center ${
              expanded ? "" : "hidden"
            }`}
          />
          {!props.onlyShowTotals && (
            <>
              <div
                className={`mb-8 overflow-y-scroll max-h-50 transition duration-100 ${
                  expanded ? "" : "hidden"
                }`}
              >
                {Object.values(cartDetails.families).find(
                  (d) => d.items.length > 0
                ) && (
                  <>
                    <div className="text-sm">
                      <span>
                        {cartDetails.licenseUsage?.isEducationalLicense
                          ? "Educational "
                          : ""}
                        License Holder: {cartDetails.licenseHolder?.name}
                      </span>
                    </div>
                    <div className="text-sm mb-4">
                      <span>
                        Company Size: {cartDetails.companySizeString()}
                      </span>
                    </div>
                  </>
                )}
                {Object.entries(cartDetails.families).map(
                  ([familyName, familyDetails]) => {
                    if (familyDetails.items.length === 0) {
                      return;
                    }

                    return (
                      <>
                        <FontFamilySection
                          familyName={familyName}
                          familyDetails={familyDetails}
                          cartDetails={cartDetails}
                        />
                        <hr className="my-4 w-full flex-row items-center justify-center" />
                      </>
                    );
                  }
                )}
                {cartDetails.physicalProducts.map(
                  (product: PhysicalProductState) => {
                    const productDetails = physicalProducts.find(
                      (p) => p.id === product.contentfulId
                    );
                    if (!productDetails) {
                      return;
                    }

                    return (
                      <>
                        <PhysicalProductSection
                          product={product}
                          productDetails={productDetails}
                          discountCents={productDiscounts[product.contentfulId]}
                          disableEdits
                        />
                        <hr className="my-4 w-full flex-row items-center justify-center" />
                      </>
                    );
                  }
                )}
              </div>
            </>
          )}
          <section className={expanded ? "" : "hidden"}>
            <div className="flex flex-row justify-between mb-2">
              <span>Subtotal</span>
              <span>{formatMoney("USD", totalDetails.subtotal)}</span>
            </div>
            {totalDetails.styleDiscountsTotal > 0 && (
              <>
                <div className="flex flex-row justify-between color-green mb-2">
                  <span>Bundle Discounts</span>
                  <span>
                    -{formatMoney("USD", totalDetails.styleDiscountsTotal)}
                  </span>
                </div>
              </>
            )}
            {totalDetails.productDiscountsTotal > 0 && (
              <div className="flex flex-row justify-between color-green mb-2">
                <span>Product Discounts</span>
                <span>
                  -{formatMoney("USD", totalDetails.productDiscountsTotal)}
                </span>
              </div>
            )}
            {activeDiscount && (
              <div className="flex flex-row justify-between mb-2 color-green">
                <span>Discount ({activeDiscount.discountCode})</span>
                <span>-{formatMoney("USD", activeDiscountCost)}</span>
              </div>
            )}
            {shippingCost != null && props.checkoutDetails ? (
              <>
                <div className="flex flex-row justify-between mb-2">
                  <span>Shipping</span>
                  <span className="opacity-50">
                    {shippingCost === 0
                      ? "free ✿"
                      : formatMoney("USD", shippingCost)}
                  </span>
                </div>
                <div className="flex flex-row justify-between mb-2">
                  <span>Tax</span>
                  {props.checkoutDetails.addressState ? (
                    <span className="opacity-50">
                      {formatMoney("USD", taxCost)}
                    </span>
                  ) : (
                    <span className="opacity-50">enter shipping address</span>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="flex flex-row justify-between mb-2">
                  <span>Shipping</span>
                  <span className="opacity-50">
                    {totalDetails.total - activeDiscountCost >= 5000
                      ? "free ✿"
                      : "Calculated on checkout"}
                  </span>
                </div>
                <div className="flex flex-row justify-between mb-2">
                  <span>Tax</span>
                  <span className="opacity-50">Calculated on checkout</span>
                </div>
              </>
            )}
            {!activeDiscount && (
              <div
                className={`flex flex-row mt-4 ${
                  props.onlyShowTotals ? "mb-2" : "mb-4"
                }`}
              >
                <input
                  id="discount_code"
                  name="discount_code"
                  type="text"
                  className="w-full border border-r-0 py-2 px-3 bg-secondary outline-none"
                  placeholder="Enter discount code"
                />
                <Button
                  rounded={false}
                  onClick={() => {
                    const input = document.getElementById(
                      "discount_code"
                    ) as HTMLInputElement;

                    const discount = getDiscount(input.value);
                    if (!discount) {
                      toast.error(
                        "This discount code does not exist or has expired."
                      );
                      return;
                    }

                    saveDiscountCode(input.value);
                    propagateCartDetails();
                    toast.success(
                      "Your discount code has been applied to the cart."
                    );
                  }}
                >
                  Apply
                </Button>
              </div>
            )}
            {activeDiscount && (
              <span
                className={`text-sm opacity-50 cursor-pointer underline underline-offset-4 color-blue-hover w-full block ${
                  props.onlyShowTotals ? "mt-4" : "mt-4 mb-2"
                }`}
                onClick={() => {
                  removeDiscountCode();
                  propagateCartDetails();
                }}
              >
                remove discount
              </span>
            )}
          </section>
          {!props.onlyShowTotals && (
            <>
              <hr className="mt-2 mb-4 border-primary" />
              <div className="flex flex-row justify-between">
                <span className="t6">Total</span>
                <span className="t6">
                  {formatMoney(
                    "USD",
                    totalDetails.total -
                      activeDiscountCost +
                      (shippingCost || 0) +
                      (taxCost || 0)
                  )}
                </span>
              </div>
            </>
          )}
          {props.action && (
            <>
              <article className="mt-4 flex flex-col gap-2">
                <button
                  onClick={handleClick}
                  className="bg-emerald-500 text-white border hover:bg-emerald-600 transition-colors duration-500 text-emerald-500 py-3 px-5 rounded-md w-100 disabled:bg-slate-300 disabled:cursor-not-allowed disabled:text-white"
                >
                  {props.action == "review" ? "Review Cart" : "Checkout"}
                </button>
                {process.env.NEXT_PUBLIC_ENABLE_FOUNDRY && (
                  <>
                    <button
                      onClick={handleShareCart}
                      className="bg-emerald-100 hover:bg-emerald-500 hover:text-white transition-colors duration-500 text-emerald-500 py-3 px-5 rounded-md w-100 disabled:bg-slate-300 disabled:cursor-not-allowed disabled:text-white"
                    >
                      Share Cart as Link
                    </button>
                    <PDFDownloadLink
                      className="w-full"
                      document={<CartQuotePdf cartDetails={cartDetails} />}
                      fileName={`spacetype-quote-${dayjs().format(
                        "MMMDDYYYY-HHmm"
                      )}.pdf`}
                    >
                      <button
                        // onClick={handleSaveQuote}
                        className="w-full bg-emerald-100 hover:bg-emerald-500 hover:text-white transition-colors duration-500 text-emerald-500 py-3 px-5 rounded-md w-100 disabled:bg-slate-300 disabled:cursor-not-allowed disabled:text-white"
                      >
                        Save Quote as PDF
                      </button>
                    </PDFDownloadLink>
                  </>
                )}
              </article>
            </>
          )}
        </>
      ) : (
        <p className="p-5">You have no items in your cart</p>
      )}
    </div>
  );
}
